import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, X, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRL = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const EVENT_LABELS = {
  impression: "Impressão", reach_unique: "Alcance único",
  link_click: "Clique no link", landing_page_view: "Visita à página de destino",
  like: "Curtida", comment: "Comentário", share: "Compartilhamento",
  video_view_50: "Visualização de vídeo (50%)", message_reply: "Resposta em mensagem",
  engagement: "Engajamento",
  lead_form_submit: "Envio de formulário", whatsapp_click: "Clique no WhatsApp",
  contact: "Contato", lead: "Lead",
  app_install: "Instalação do app", app_registration: "Cadastro no app",
  app_event: "Evento do app",
  purchase: "Compra", initiate_checkout: "Iniciar checkout", add_to_cart: "Adicionar ao carrinho",
};

const CTA_OPTIONS = [
  { value: "saiba_mais", label: "Saiba mais" },
  { value: "comprar", label: "Comprar" },
  { value: "cadastrar", label: "Cadastre-se" },
  { value: "mensagem", label: "Enviar mensagem" },
  { value: "instalar", label: "Instalar" },
  { value: "ligar", label: "Ligar agora" },
  { value: "enviar_whats", label: "Enviar WhatsApp" },
];

const PLACEMENTS = [
  { value: "feed", label: "Feed" },
  { value: "stories", label: "Stories" },
  { value: "reels", label: "Reels" },
  { value: "sidebar", label: "Sidebar" },
  { value: "in_video", label: "In-Video" },
];

const STEPS = ["Objetivo", "Conjunto", "Criativo", "Revisão"];

export default function AdsWizard({ open, onOpenChange, onDone, addToCampaignId = null, initialAd = null }) {
  const [step, setStep] = useState(0);
  const [objectives, setObjectives] = useState([]);
  const [pixels, setPixels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [interestQuery, setInterestQuery] = useState("");
  const [interestResults, setInterestResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [existingCampaign, setExistingCampaign] = useState(null);

  // Campaign
  const [campaign, setCampaign] = useState({
    name: "", objective: "traffic", cbo_enabled: false, cbo_daily_budget: null, notes: "",
  });
  // Ad Set
  const [adSet, setAdSet] = useState({
    name: "", budget_type: "daily", daily_budget: 20, total_budget: null,
    start_at: new Date().toISOString().slice(0, 16),
    end_at: null, schedule_hours: [],
    bid_strategy: "auto", bid_cap_brl: null,
    optimization_event: "landing_page_view",
    targeting: {
      age_min: 18, age_max: 55, genders: ["all"], locations: [],
      interests: [], behaviors: [], profile_types: [], devices: [],
    },
    exclusions: { interests: [], locations: [], age_ranges: [], profile_types: [], custom_audiences: [], exclude_converters: false },
    placements: ["feed"],
  });
  // Ad
  const [ad, setAd] = useState({
    name: "", format: "image",
    creative: { title: "", primary_text: "", description: "", media: [{ url: "", type: "image", aspect_ratio: "1:1" }] },
    cta: "saiba_mais", destination_url: "https://",
  });
  const [locationInput, setLocationInput] = useState("");
  const [createdIds, setCreatedIds] = useState({});
  const [selectedPixelId, setSelectedPixelId] = useState("");

  useEffect(() => {
    if (!open) return;
    // Reset when opening
    setCreatedIds({});
    if (addToCampaignId) {
      // Skip step 0 (Objetivo) — jump directly to Conjunto for the existing campaign
      axios.get(`${API}/ads2/campaigns/${addToCampaignId}`).then((r) => {
        setExistingCampaign(r.data);
        setCampaign({
          name: r.data.name, objective: r.data.objective,
          cbo_enabled: !!r.data.cbo_enabled, cbo_daily_budget: r.data.cbo_daily_budget, notes: r.data.notes || "",
        });
        setStep(1);
      }).catch(() => setStep(0));
    } else {
      setExistingCampaign(null);
      setStep(0);
    }
    axios.get(`${API}/ads2/objectives`).then((r) => setObjectives(r.data)).catch(() => {});
    axios.get(`${API}/ads2/pixels`).then((r) => setPixels(r.data || [])).catch(() => setPixels([]));
    // Prefill from a post (boost flow) — only when creating a brand new campaign
    if (initialAd && !addToCampaignId) {
      setAd((a) => ({
        ...a,
        name: initialAd.name || a.name,
        format: initialAd.format || a.format,
        cta: initialAd.cta || a.cta,
        destination_url: initialAd.destination_url || a.destination_url,
        creative: {
          ...a.creative,
          title: initialAd.creative?.title || a.creative.title,
          primary_text: initialAd.creative?.primary_text || a.creative.primary_text,
          description: initialAd.creative?.description || a.creative.description,
          media: initialAd.creative?.media || a.creative.media,
        },
      }));
      setCampaign((c) => ({ ...c, name: initialAd.name || c.name }));
    }
  }, [open, addToCampaignId, initialAd]);

  const objMeta = objectives.find((o) => o.key === campaign.objective);

  useEffect(() => {
    // when objective changes, reset optimization_event to primary
    if (objMeta) {
      setAdSet((s) => ({ ...s, optimization_event: objMeta.primary }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.objective, objectives]);

  const searchInterests = async (q) => {
    setInterestQuery(q);
    if (!q || q.length < 2) { setInterestResults([]); return; }
    try {
      const r = await axios.get(`${API}/ads2/targeting/suggest`, { params: { q, kind: "interest" } });
      setInterestResults(r.data || []);
    } catch { /* empty */ }
  };

  const addInterest = (name, target = "targeting") => {
    setAdSet((s) => {
      if (target === "targeting") {
        if (s.targeting.interests.includes(name)) return s;
        return { ...s, targeting: { ...s.targeting, interests: [...s.targeting.interests, name] } };
      } else {
        if (s.exclusions.interests.includes(name)) return s;
        return { ...s, exclusions: { ...s.exclusions, interests: [...s.exclusions.interests, name] } };
      }
    });
    setInterestQuery(""); setInterestResults([]);
  };
  const removeInterest = (name, target = "targeting") => {
    setAdSet((s) => target === "targeting"
      ? { ...s, targeting: { ...s.targeting, interests: s.targeting.interests.filter((i) => i !== name) } }
      : { ...s, exclusions: { ...s.exclusions, interests: s.exclusions.interests.filter((i) => i !== name) } });
  };

  const addLocation = () => {
    if (!locationInput.trim()) return;
    setAdSet((s) => ({
      ...s,
      targeting: { ...s.targeting, locations: [...s.targeting.locations, { type: "city", name: locationInput.trim() }] },
    }));
    setLocationInput("");
  };
  const removeLocation = (idx) => {
    setAdSet((s) => ({ ...s, targeting: { ...s.targeting, locations: s.targeting.locations.filter((_, i) => i !== idx) } }));
  };

  const togglePlacement = (p) => {
    setAdSet((s) => ({
      ...s,
      placements: s.placements.includes(p) ? s.placements.filter((x) => x !== p) : [...s.placements, p],
    }));
  };

  const ceiling = objMeta?.ceiling || 0;
  const bidMin = objMeta?.ref_min || 0;
  const bidRefMax = objMeta?.ref_max || 0;

  // ================== step navigation ==================
  const canNext = () => {
    if (step === 0) return !!campaign.name && !!campaign.objective;
    if (step === 1) {
      if (!adSet.name) return false;
      if (adSet.budget_type === "daily" && !adSet.daily_budget) return false;
      if (adSet.budget_type === "total" && !adSet.total_budget) return false;
      if (adSet.placements.length === 0) return false;
      if (adSet.bid_strategy === "manual") {
        if (!adSet.bid_cap_brl) return false;
        if (adSet.bid_cap_brl < bidMin || adSet.bid_cap_brl > ceiling) return false;
      }
      return true;
    }
    if (step === 2) {
      return !!ad.name && !!ad.creative.title && !!ad.creative.primary_text && !!ad.creative.media[0]?.url && !!ad.destination_url;
    }
    return true;
  };

  const publish = async () => {
    setBusy(true);
    try {
      let campaign_id;
      let ad_set_id;
      const reuseAdSet = !!createdIds.reuse_ad_set && !!createdIds.ad_set_id;
      if (reuseAdSet) {
        // "+ Mais um anúncio": reuse existing campaign AND ad set — only create a new ad below.
        campaign_id = createdIds.campaign_id;
        ad_set_id = createdIds.ad_set_id;
      } else if (existingCampaign) {
        campaign_id = existingCampaign.campaign_id;
      } else {
        // 1) create campaign
        const cResp = await axios.post(`${API}/ads2/campaigns`, {
          name: campaign.name, objective: campaign.objective, status: "draft",
          cbo_enabled: campaign.cbo_enabled,
          cbo_daily_budget: campaign.cbo_enabled ? Number(campaign.cbo_daily_budget) : null,
          notes: campaign.notes || "",
        });
        campaign_id = cResp.data.campaign_id;
      }
      if (!reuseAdSet) {
        // 2) ad set (respects optional selected pixel)
        const adSetPayload = {
          ...adSet, campaign_id,
          daily_budget: adSet.budget_type === "daily" ? Number(adSet.daily_budget) : null,
          total_budget: adSet.budget_type === "total" ? Number(adSet.total_budget) : null,
          bid_cap_brl: adSet.bid_strategy === "manual" ? Number(adSet.bid_cap_brl) : null,
          start_at: new Date(adSet.start_at).toISOString(),
          end_at: adSet.end_at ? new Date(adSet.end_at).toISOString() : null,
          pixel_id: selectedPixelId || null,
        };
        const asResp = await axios.post(`${API}/ads2/ad-sets`, adSetPayload);
        ad_set_id = asResp.data.ad_set_id;
      }
      // 3) ad
      const adResp = await axios.post(`${API}/ads2/ads`, {
        ...ad, ad_set_id,
      });
      const ad_id = adResp.data.ad_id;
      // 4) activate the hierarchy (only what's new)
      await Promise.all([
        (existingCampaign || reuseAdSet) ? Promise.resolve() : axios.patch(`${API}/ads2/campaigns/${campaign_id}/status`, { status: "active" }),
        reuseAdSet ? Promise.resolve() : axios.patch(`${API}/ads2/ad-sets/${ad_set_id}/status`, { status: "active" }),
        axios.patch(`${API}/ads2/ads/${ad_id}/status`, { status: "active" }),
      ]);
      setCreatedIds({ campaign_id, ad_set_id, ad_id });
      toast.success(
        reuseAdSet ? "Anúncio adicionado ao conjunto!"
        : existingCampaign ? "Novo conjunto publicado!"
        : "Campanha publicada! Criativo em revisão (até 24h)."
      );
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Erro ao publicar: ${detail}`);
    }
    setBusy(false);
  };

  const addAnotherAd = () => {
    // Reuse the same ad set — only reset the creative and mark the ad-set as "already existing"
    // so publish() skips ad-set creation and reuses createdIds.ad_set_id.
    setCreatedIds({ ...createdIds, ad_id: null, reuse_ad_set: true });
    setAd({
      name: "", format: "image",
      creative: { title: "", primary_text: "", description: "", media: [{ url: "", type: "image", aspect_ratio: "1:1" }] },
      cta: "saiba_mais", destination_url: "https://",
    });
    setStep(2);
  };

  const addAnotherAdSet = () => {
    // Keep campaign, reset ad set + ad
    const kept = createdIds.campaign_id;
    setCreatedIds({ campaign_id: kept });
    setAdSet({
      name: "", budget_type: "daily", daily_budget: 20, total_budget: null,
      start_at: new Date().toISOString().slice(0, 16),
      end_at: null, schedule_hours: [],
      bid_strategy: "auto", bid_cap_brl: null,
      optimization_event: objMeta?.primary || "landing_page_view",
      targeting: {
        age_min: 18, age_max: 55, genders: ["all"], locations: [],
        interests: [], behaviors: [], profile_types: [], devices: [],
      },
      exclusions: { interests: [], locations: [], age_ranges: [], profile_types: [], custom_audiences: [], exclude_converters: false },
      placements: ["feed"],
    });
    setAd({
      name: "", format: "image",
      creative: { title: "", primary_text: "", description: "", media: [{ url: "", type: "image", aspect_ratio: "1:1" }] },
      cta: "saiba_mais", destination_url: "https://",
    });
    setSelectedPixelId("");
    // Trick: mark campaign as existing so the next publish reuses it
    setExistingCampaign({ campaign_id: kept, name: campaign.name, objective: campaign.objective });
    setStep(1);
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rawUrl = r.data?.url || r.data?.file_url || r.data?.path;
      // Store relative path (/api/files/...); browser resolves against origin,
      // so the ad media URL works on both preview and production domains.
      const url = rawUrl?.startsWith("http") ? rawUrl : rawUrl;
      const isVideo = file.type.startsWith("video/");
      setAd((a) => ({
        ...a,
        format: isVideo ? "video" : (a.format === "video" ? "image" : a.format),
        creative: { ...a.creative, media: [{ url, type: isVideo ? "video" : "image", aspect_ratio: "1:1" }] },
      }));
      toast.success("Mídia enviada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    }
    setUploading(false);
  };

  const fetchEstimate = async () => {
    // Requires an ad_set already existing; we approximate via a dry-run by creating draft and calling estimate.
    // For simplicity we skip until publish; the review shows the ceiling instead.
    setEstimate(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ads-wizard-dialog">
        <DialogHeader>
          <DialogTitle>
            {existingCampaign
              ? `Adicionar ao conjunto — ${existingCampaign.name} · Passo ${step + 1} de 4: ${STEPS[step]}`
              : `Nova campanha — Passo ${step + 1} de 4: ${STEPS[step]}`}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-slate-200"}`} />
            </div>
          ))}
        </div>

        {/* Step 0: Objective */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da campanha</Label>
              <Input value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                placeholder="Ex.: Lançamento Verão 2026" data-testid="wiz-campaign-name" />
            </div>
            <div className="space-y-2">
              <Label>Objetivo</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {objectives.map((o) => (
                  <button key={o.key} type="button"
                    onClick={() => setCampaign({ ...campaign, objective: o.key })}
                    className={`text-left border rounded-lg p-3 transition ${campaign.objective === o.key ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "hover:bg-accent"}`}
                    data-testid={`wiz-obj-${o.key}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{o.label}</span>
                      {campaign.objective === o.key && <Check className="w-4 h-4 text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{o.when}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Ref.: {BRL(o.ref_min)}–{BRL(o.ref_max)} / {o.unit} · Teto: <b>{BRL(o.ceiling)}</b>
                    </p>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 p-3 border rounded-lg">
              <div>
                <Label className="mb-0">Orçamento na campanha (CBO)</Label>
                <p className="text-xs text-muted-foreground">Distribui o orçamento entre os conjuntos automaticamente.</p>
              </div>
              <Switch checked={campaign.cbo_enabled} onCheckedChange={(v) => setCampaign({ ...campaign, cbo_enabled: v })} data-testid="wiz-cbo-toggle" />
            </div>
            {campaign.cbo_enabled && (
              <div className="space-y-2">
                <Label>Orçamento diário CBO (R$)</Label>
                <Input type="number" min={5} value={campaign.cbo_daily_budget || ""}
                  onChange={(e) => setCampaign({ ...campaign, cbo_daily_budget: e.target.value })} data-testid="wiz-cbo-budget" />
              </div>
            )}
          </div>
        )}

        {/* Step 1: Ad Set */}
        {step === 1 && objMeta && (
          <div className="space-y-4">
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                <b>Objetivo:</b> {objMeta.label}. Teto máximo: <b>{BRL(ceiling)}</b>. Você nunca paga acima disso, mesmo que outro anunciante lance mais.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2 md:col-span-2">
                <Label>Nome do conjunto</Label>
                <Input value={adSet.name} onChange={(e) => setAdSet({ ...adSet, name: e.target.value })}
                  placeholder="Ex.: SP capital · 25-45 · interesse pizza" data-testid="wiz-adset-name" />
              </div>

              {!campaign.cbo_enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Tipo de orçamento</Label>
                    <Select value={adSet.budget_type} onValueChange={(v) => setAdSet({ ...adSet, budget_type: v })}>
                      <SelectTrigger data-testid="wiz-budget-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Diário</SelectItem>
                        <SelectItem value="total">Total (vitalício)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Valor (R$) — mín. {adSet.budget_type === "daily" ? BRL(5) : BRL(20)}</Label>
                    <Input type="number" min={adSet.budget_type === "daily" ? 5 : 20}
                      value={adSet.budget_type === "daily" ? (adSet.daily_budget || "") : (adSet.total_budget || "")}
                      onChange={(e) => setAdSet({
                        ...adSet,
                        [adSet.budget_type === "daily" ? "daily_budget" : "total_budget"]: e.target.value,
                      })}
                      data-testid="wiz-budget-value" />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Início</Label>
                <Input type="datetime-local" value={adSet.start_at}
                  onChange={(e) => setAdSet({ ...adSet, start_at: e.target.value })} data-testid="wiz-start-at" />
              </div>
              <div className="space-y-2">
                <Label>Fim (opcional)</Label>
                <Input type="datetime-local" value={adSet.end_at || ""}
                  onChange={(e) => setAdSet({ ...adSet, end_at: e.target.value })} />
              </div>

              <div className="space-y-2">
                <Label>Estratégia de lance</Label>
                <Select value={adSet.bid_strategy} onValueChange={(v) => setAdSet({ ...adSet, bid_strategy: v })}>
                  <SelectTrigger data-testid="wiz-bid-strategy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático (menor custo)</SelectItem>
                    <SelectItem value="manual">Manual (com teto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {adSet.bid_strategy === "manual" && (
                <div className="space-y-2">
                  <Label>Lance máx. ({BRL(bidMin)}–{BRL(ceiling)})</Label>
                  <Input type="number" step="0.01" min={bidMin} max={ceiling}
                    value={adSet.bid_cap_brl || ""}
                    onChange={(e) => setAdSet({ ...adSet, bid_cap_brl: e.target.value })}
                    data-testid="wiz-bid-cap" />
                  <p className="text-xs text-muted-foreground">O leilão organiza a entrega, não infla o preço.</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Otimizar por</Label>
                <Select value={adSet.optimization_event} onValueChange={(v) => setAdSet({ ...adSet, optimization_event: v })}>
                  <SelectTrigger data-testid="wiz-opt-event"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {objMeta.events.map((ev) => <SelectItem key={ev} value={ev}>{EVENT_LABELS[ev] || ev}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Pixel de conversão (opcional)</Label>
                <Select value={selectedPixelId || "__none__"} onValueChange={(v) => setSelectedPixelId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="wiz-pixel"><SelectValue placeholder="Sem pixel" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sem pixel</SelectItem>
                    {pixels.map((p) => (
                      <SelectItem key={p.pixel_id} value={p.pixel_id}>
                        {p.name} — {p.platform === "own" ? "FUTURO PDV" : p.platform === "meta" ? "Meta" : p.platform === "tiktok" ? "TikTok" : "Google"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Vincule um pixel para medir as conversões deste conjunto. Você pode criar pixels ilimitados em /ads-manager/pixels.</p>
              </div>

              <div className="space-y-2">
                <Label>Idade</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={13} max={65} value={adSet.targeting.age_min}
                    onChange={(e) => setAdSet({ ...adSet, targeting: { ...adSet.targeting, age_min: Number(e.target.value) } })}
                    data-testid="wiz-age-min" />
                  <span>—</span>
                  <Input type="number" min={13} max={65} value={adSet.targeting.age_max}
                    onChange={(e) => setAdSet({ ...adSet, targeting: { ...adSet.targeting, age_max: Number(e.target.value) } })}
                    data-testid="wiz-age-max" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Gênero</Label>
                <Select value={adSet.targeting.genders[0]} onValueChange={(v) => setAdSet({ ...adSet, targeting: { ...adSet.targeting, genders: [v] } })}>
                  <SelectTrigger data-testid="wiz-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="m">Masculino</SelectItem>
                    <SelectItem value="f">Feminino</SelectItem>
                    <SelectItem value="nb">Não-binário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Locations */}
            <div className="space-y-2">
              <Label>Locais (cidades)</Label>
              <div className="flex gap-2">
                <Input value={locationInput} onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }}
                  placeholder="Ex.: São Paulo" data-testid="wiz-location-input" />
                <Button type="button" variant="outline" onClick={addLocation} data-testid="wiz-add-location">Adicionar</Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {adSet.targeting.locations.map((l, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {l.name} <button onClick={() => removeLocation(i)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Interests: include/exclude */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InterestPanel
                title="Incluir interesses" color="emerald"
                items={adSet.targeting.interests}
                onRemove={(n) => removeInterest(n, "targeting")}
                query={interestQuery} results={interestResults}
                onSearch={searchInterests}
                onAdd={(n) => addInterest(n, "targeting")}
                testid="include"
              />
              <InterestPanel
                title="Excluir interesses" color="rose"
                items={adSet.exclusions.interests}
                onRemove={(n) => removeInterest(n, "exclusions")}
                query={interestQuery} results={interestResults}
                onSearch={searchInterests}
                onAdd={(n) => addInterest(n, "exclusions")}
                testid="exclude"
              />
            </div>

            {/* Placements */}
            <div className="space-y-2">
              <Label>Posicionamentos</Label>
              <div className="flex flex-wrap gap-2">
                {PLACEMENTS.map((p) => (
                  <button key={p.value} type="button" onClick={() => togglePlacement(p.value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${adSet.placements.includes(p.value) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"}`}
                    data-testid={`wiz-placement-${p.value}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Creative */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do anúncio</Label>
              <Input value={ad.name} onChange={(e) => setAd({ ...ad, name: e.target.value })}
                placeholder="Ex.: Anúncio Verão v1" data-testid="wiz-ad-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Formato</Label>
                <Select value={ad.format} onValueChange={(v) => setAd({ ...ad, format: v })}>
                  <SelectTrigger data-testid="wiz-ad-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Imagem</SelectItem>
                    <SelectItem value="video">Vídeo</SelectItem>
                    <SelectItem value="carousel">Carrossel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>CTA</Label>
                <Select value={ad.cta} onValueChange={(v) => setAd({ ...ad, cta: v })}>
                  <SelectTrigger data-testid="wiz-ad-cta"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CTA_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Mídia do anúncio</Label>
                <div className="flex gap-2 items-center">
                  <Input value={ad.creative.media[0].url}
                    onChange={(e) => setAd({ ...ad, creative: { ...ad.creative, media: [{ ...ad.creative.media[0], url: e.target.value, type: ad.format === "video" ? "video" : "image" }] } })}
                    placeholder="Cole uma URL ou faça upload ao lado" data-testid="wiz-ad-media-url" />
                  <label className="inline-flex items-center gap-1 text-sm cursor-pointer px-3 py-2 rounded-md border hover:bg-accent">
                    {uploading ? "Enviando..." : "Enviar arquivo"}
                    <input type="file" accept="image/*,video/*" className="hidden"
                      onChange={(e) => uploadMedia(e.target.files?.[0])}
                      data-testid="wiz-ad-file-upload" disabled={uploading} />
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground">Formatos: JPG, PNG, WebP, GIF, MP4, WebM, MOV (até 50MB para vídeo, 10MB para imagem).</p>
              </div>
              <div className="space-y-2">
                <Label>Título (máx 40)</Label>
                <Input maxLength={40} value={ad.creative.title}
                  onChange={(e) => setAd({ ...ad, creative: { ...ad.creative, title: e.target.value } })}
                  data-testid="wiz-ad-title" />
              </div>
              <div className="space-y-2">
                <Label>Descrição (máx 90, opcional)</Label>
                <Input maxLength={90} value={ad.creative.description}
                  onChange={(e) => setAd({ ...ad, creative: { ...ad.creative, description: e.target.value } })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Texto principal (máx 280)</Label>
                <Textarea maxLength={280} value={ad.creative.primary_text}
                  onChange={(e) => setAd({ ...ad, creative: { ...ad.creative, primary_text: e.target.value } })}
                  rows={3} data-testid="wiz-ad-body" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>URL de destino (https://)</Label>
                <Input value={ad.destination_url}
                  onChange={(e) => setAd({ ...ad, destination_url: e.target.value })}
                  placeholder="https://seusite.com/promo" data-testid="wiz-ad-url" />
              </div>
            </div>

            {/* Preview */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-2">Prévia no feed</p>
                <div className="border rounded-lg overflow-hidden max-w-sm">
                  {ad.creative.media[0].url && (
                    <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                      {ad.format === "video"
                        ? <video src={ad.creative.media[0].url} className="w-full h-full object-cover" muted />
                        : <img src={ad.creative.media[0].url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                    </div>
                  )}
                  <div className="p-3 space-y-1">
                    <p className="font-semibold text-sm">{ad.creative.title || "Título do anúncio"}</p>
                    <p className="text-xs text-muted-foreground">{ad.creative.primary_text || "Texto principal..."}</p>
                    <Button size="sm" variant="default" className="w-full mt-2">
                      {CTA_OPTIONS.find((c) => c.value === ad.cta)?.label}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && objMeta && (
          <div className="space-y-4">
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                <b>O leilão só organiza a entrega.</b> Você nunca paga acima do teto: <b>{BRL(ceiling)} / {objMeta.unit}</b>.
              </AlertDescription>
            </Alert>
            <ReviewRow label="Nome" value={campaign.name} />
            <ReviewRow label="Objetivo" value={objMeta.label} />
            <ReviewRow label="Otimização" value={EVENT_LABELS[adSet.optimization_event] || adSet.optimization_event} />
            <ReviewRow label="Orçamento" value={
              campaign.cbo_enabled ? `CBO ${BRL(campaign.cbo_daily_budget)}/dia`
                : adSet.budget_type === "daily" ? `${BRL(adSet.daily_budget)}/dia` : `${BRL(adSet.total_budget)} total`
            } />
            <ReviewRow label="Lance" value={adSet.bid_strategy === "auto" ? "Automático" : `Manual — máx ${BRL(adSet.bid_cap_brl)}`} />
            <ReviewRow label="Público" value={`${adSet.targeting.age_min}–${adSet.targeting.age_max} anos · ${adSet.targeting.locations.map((l) => l.name).join(", ") || "Todo o Brasil"}`} />
            <ReviewRow label="Interesses" value={adSet.targeting.interests.join(", ") || "—"} />
            <ReviewRow label="Excluir" value={adSet.exclusions.interests.join(", ") || "—"} />
            <ReviewRow label="Posicionamentos" value={adSet.placements.join(", ")} />
            <ReviewRow label="Criativo" value={ad.name} />
            <ReviewRow label="CTA" value={CTA_OPTIONS.find((c) => c.value === ad.cta)?.label} />
            <ReviewRow label="Destino" value={ad.destination_url} />
            {createdIds.campaign_id && (
              <Alert className="border-emerald-500/40 bg-emerald-50/30">
                <Check className="w-4 h-4 text-emerald-600" />
                <AlertDescription>Publicada! Campanha ID: <b>{createdIds.campaign_id}</b></AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between !flex-row">
          <Button variant="outline" onClick={() => setStep(Math.max((existingCampaign ? 1 : 0), step - 1))} disabled={step <= (existingCampaign ? 1 : 0) || busy} data-testid="wiz-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext() || busy} data-testid="wiz-next">
              Avançar <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : !createdIds.ad_id ? (
            <Button onClick={publish} disabled={busy} data-testid="wiz-publish">
              {busy ? "Publicando..." : "Publicar (revisão em até 24h)"}
            </Button>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={addAnotherAd} data-testid="wiz-add-another-ad">
                + Mais um anúncio
              </Button>
              <Button variant="outline" onClick={addAnotherAdSet} data-testid="wiz-add-another-adset">
                + Novo conjunto
              </Button>
              <Button onClick={() => { onDone?.(); onOpenChange(false); }} data-testid="wiz-finalize">
                Concluir
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-2 border-b last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right ml-4 max-w-[60%] truncate">{value || "—"}</span>
    </div>
  );
}

function InterestPanel({ title, color, items, onRemove, query, results, onSearch, onAdd, testid }) {
  const [localQuery, setLocalQuery] = useState("");
  const [localResults, setLocalResults] = useState([]);
  const doSearch = async (v) => {
    setLocalQuery(v);
    if (v.length < 2) { setLocalResults([]); return; }
    try {
      const r = await axios.get(`${API}/ads2/targeting/suggest`, { params: { q: v, kind: "interest" } });
      setLocalResults(r.data || []);
    } catch { /* empty */ }
  };
  const colorClass = color === "emerald"
    ? "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10"
    : "border-rose-300 bg-rose-50/50 dark:bg-rose-900/10";
  return (
    <div className={`border rounded-lg p-3 ${colorClass}`}>
      <p className="text-sm font-semibold mb-2">{title}</p>
      <Input value={localQuery} onChange={(e) => doSearch(e.target.value)} placeholder="Buscar interesse..." data-testid={`wiz-int-${testid}`} />
      {localResults.length > 0 && (
        <div className="mt-1 max-h-32 overflow-y-auto space-y-1">
          {localResults.map((r) => (
            <button key={r.name} type="button" onClick={() => { onAdd(r.name); setLocalQuery(""); setLocalResults([]); }}
              className="w-full text-left text-sm px-2 py-1 rounded hover:bg-white/60">
              {r.name} <span className="text-xs text-muted-foreground">({(r.reach / 1e6).toFixed(1)}M)</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-2">
        {items.map((n) => (
          <Badge key={n} variant="secondary" className="gap-1">
            {n} <button onClick={() => onRemove(n)}><X className="w-3 h-3" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
